import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";

import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery as useBaseQuery,
} from "@tanstack/react-query";

import { fetcher, HttpError, type ApiResponse, type ApiShape } from "./fetcher";

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

export const deepAssign = <T extends { [key: string | number]: any }>(
  target: T,
  source?: DeepPartial<T>,
): T => {
  if (!source) {
    return target;
  }

  for (const key in source) {
    if (typeof source[key] === "object") {
      if (!target[key]) {
        target[key as keyof T] = {} as T[keyof T];
      }
      deepAssign(target[key], source[key] as DeepPartial<T[keyof T]>);
    } else if (source[key] !== undefined) {
      target[key as keyof T] = source[key] as T[keyof T];
    }
  }

  return target;
};

type ApiContextType = {
  fetch?: (request: Request) => Response | Promise<Response>;
  queryOptions: { staleTime?: number };
  onActionError?: (error: Error) => void;
};

const createApiContext = () => {
  return createContext<ApiContextType | undefined>(undefined);
};

export const createApi = <Api extends ApiShape>({
  baseUrl,
}: {
  baseUrl: string;
}) => {
  const queryKey = uuidv4();

  const queryClient = new QueryClient();

  const ApiContext = createApiContext();

  const defaultOptions = deepAssign(queryClient.getDefaultOptions(), {
    queries: {
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      retry: (failureCount: number, error: unknown) => {
        if (
          error instanceof HttpError &&
          (error.status >= 500 || error.status === 429)
        ) {
          return failureCount < 3;
        }
        return false;
      },
      suspense: true,
      useErrorBoundary: true,
    },
    mutations: {
      useErrorBoundary: true,
    },
  });

  type QueriesOptions = typeof defaultOptions.queries;

  type ModifiedQueriesOptions = Omit<QueriesOptions, "refetchOnMount"> & {
    refetchOnMount: boolean;
  };

  type MutationOptions = typeof defaultOptions.mutations;

  type ModifiedMutationOptions = Omit<MutationOptions, "variables"> & {
    variables: (variables: unknown) => unknown;
  };

  const useQuery: {
    <Url extends keyof Api, Method extends keyof Api[Url]>(
      request: { url: Url; method: Method } & {
        [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
      },
      options?: {
        refetchInterval?:
          | number
          | false
          | ((
              response: ApiResponse<Api, Url, Method> | undefined,
            ) => number | false);
      },
    ): {
      response: ApiResponse<Api, Url, Method>;
      isFetching: boolean;
      refetch: () => Promise<void>;
    };

    <Url extends keyof Api, Method extends keyof Api[Url]>(
      request?: { url: Url; method: Method } & {
        [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
      },
      options?: {
        refetchInterval?:
          | number
          | false
          | ((
              response: ApiResponse<Api, Url, Method> | undefined,
            ) => number | false);
      },
    ): {
      response?: ApiResponse<Api, Url, Method>;
      isFetching: boolean;
      refetch: () => Promise<void>;
    };
  } = <Url extends keyof Api, Method extends keyof Api[Url]>(
    request:
      | ({ url: Url; method: Method } & {
          [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
        })
      | undefined,
    {
      refetchInterval,
    }: {
      refetchInterval?:
        | number
        | false
        | ((
            response: ApiResponse<Api, Url, Method> | undefined,
          ) => number | false);
    } = {},
  ): {
    response: ApiResponse<Api, Url, Method>;
    isFetching: boolean;
    refetch: () => Promise<void>;
  } => {
    const contextOptions = useContext(ApiContext);

    const hasRequest = Boolean(request);

    const {
      data,
      isFetching,
      refetch: baseRefetch,
    } = useBaseQuery(
      request
        ? [
            queryKey,
            request.url,
            request.method,
            request.body,
            request.headers,
            request.path,
            request.query,
          ]
        : [],
      () => {
        if (!request) {
          throw new Error("fetcher has been called with undefined request");
        }

        return fetcher({
          url: request.url,
          method: request.method,
          body: request.body,
          headers: request.headers,
          path: request.path,
          query: request.query,
          baseUrl,
          fetchModifier: contextOptions?.fetch,
        }) as Promise<ApiResponse<Api, Url, Method>>;
      },
      {
        ...(defaultOptions.queries as ModifiedQueriesOptions),
        suspense: true,
        keepPreviousData: true,
        useErrorBoundary: true,
        refetchInterval,
        enabled: hasRequest,
        networkMode: "always",
        ...contextOptions?.queryOptions,
      },
    );

    const refetch = useCallback(async () => {
      if (hasRequest) {
        await baseRefetch();
      }
    }, [baseRefetch, hasRequest]);

    return {
      response: data!,
      isFetching,
      refetch,
    };
  };

  const useOnDemandQuery = <
    Url extends keyof Api,
    Method extends keyof Api[Url],
  >(
    request:
      | ({ url: Url; method: Method } & {
          [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
        })
      | undefined,
    {
      refetchInterval,
    }: {
      refetchInterval?:
        | number
        | false
        | ((
            response: ApiResponse<Api, Url, Method> | undefined,
          ) => number | false);
    } = {},
  ): {
    response: ApiResponse<Api, Url, Method> | undefined;
    isFetching: boolean;
    refetch: () => Promise<void>;
  } => {
    const context = useContext(ApiContext);

    const hasRequest = Boolean(request);

    const {
      data,
      isFetching,
      refetch: baseRefetch,
    } = useBaseQuery(
      request
        ? [
            queryKey,
            request.url,
            request.method,
            request.body,
            request.headers,
            request.path,
            request.query,
          ]
        : [],
      () => {
        if (!request) {
          throw new Error("fetcher has been called with undefined request");
        }

        return fetcher({
          url: request.url,
          method: request.method,
          body: request.body,
          headers: request.headers,
          path: request.path,
          query: request.query,
          baseUrl,
          fetchModifier: context?.fetch,
        }) as Promise<ApiResponse<Api, Url, Method>>;
      },
      {
        ...(defaultOptions.queries as ModifiedQueriesOptions),
        useErrorBoundary: true,
        keepPreviousData: true,
        suspense: false,
        enabled: hasRequest,
        refetchInterval,
        networkMode: "always",
        ...context?.queryOptions,
      },
    );

    const refetch = useCallback(async () => {
      if (hasRequest) {
        await baseRefetch();
      }
    }, [baseRefetch, hasRequest]);

    return {
      response: data,
      isFetching,
      refetch,
    };
  };

  const useAction = <Url extends keyof Api, Method extends keyof Api[Url]>({
    url,
    method,
  }: { url: Url; method: Method } & Omit<
    Api[Url][Method]["request"],
    "body" | "headers" | "path" | "query"
  >): {
    response: ApiResponse<Api, Url, Method> | undefined;
    isFetching: boolean;
    reset: () => void;
    action: (
      request: Omit<Api[Url][Method]["request"], "url" | "method">,
      {
        onResponse,
      }?: {
        onResponse: (
          response: ApiResponse<Api, Url, Method>,
        ) => Promise<void> | void;
      },
    ) => void;
  } => {
    const context = useContext(ApiContext);

    const { data, isLoading, mutate, reset } = useMutation(
      async ({
        url,
        method,
        body,
        headers,
        path,
        query,
        onResponse,
      }: Api[Url][Method]["request"] & {
        onResponse?: (
          response: ApiResponse<Api, Url, Method>,
        ) => Promise<void> | void;
      }) => {
        const res = (await fetcher({
          url,
          method,
          body,
          headers,
          path,
          query,
          baseUrl,
          fetchModifier: context?.fetch,
        })) as ApiResponse<Api, Url, Method>;

        await onResponse?.(res);

        return res;
      },

      {
        ...(defaultOptions.mutations as ModifiedMutationOptions),
        useErrorBoundary: !context?.onActionError,
        networkMode: "always",
        onError: (error: Error) => {
          context?.onActionError?.(error);
        },
      },
    );

    const action = useCallback(
      (
        request: Omit<Api[Url][Method]["request"], "url" | "method">,
        options?: {
          onResponse: (
            response: ApiResponse<Api, Url, Method>,
          ) => Promise<void> | void;
        },
      ) =>
        mutate({
          ...({ url, method, ...request } as Api[Url][Method]["request"]),
          ...(options
            ? {
                onResponse: options.onResponse,
              }
            : {}),
        }),
      [method, url, mutate],
    );

    return {
      response: data,
      isFetching: isLoading,
      reset,
      action,
    };
  };

  const useCursorQuery = <
    Url extends {
      [Url in keyof Api]: Extract<
        Api[Url][keyof Api[Url]]["response"],
        { status: 200 }
      > extends never
        ? never
        : Url;
    }[keyof Api],
    Method extends keyof Api[Url],
    Item,
  >(
    {
      url,
      method,
      body,
      headers,
      path,
      query,
    }: { url: Url; method: Method } & {
      [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
    },
    {
      insertCursor,
      extractCursor,
      extractItems,
      refetchInterval,
    }: {
      insertCursor: (
        cursor?: string,
      ) =>
        | DeepPartial<Omit<Api[Url][Method]["request"], "url" | "method">>
        | undefined;
      extractCursor: (
        response: Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
      ) => string | undefined;
      extractItems: (
        response: Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
      ) => Item[];

      refetchInterval?: number | false;
    },
  ): {
    items: ReturnType<typeof extractItems>;
    isFetching: boolean;
    isFetchingMore: boolean;
    hasMore: boolean;
    fetchMore: () => void;
    refetch: () => Promise<void>;
  } => {
    const context = useContext(ApiContext);

    const {
      data,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      refetch: baseRefetch,
      fetchNextPage,
    } = useInfiniteQuery(
      [queryKey, "cursor", url, method, body, headers, path, query],
      async ({ pageParam: cursor }: { pageParam?: string }) => {
        const cursorPart = insertCursor(cursor);

        const requestParams = structuredClone({
          body,
          headers,
          path,
          query,
        }) as Omit<Api[Url][Method]["request"], "url" | "method">;

        const response = await fetcher({
          url,
          baseUrl,
          method,
          fetchModifier: context?.fetch,
          ...(cursorPart
            ? deepAssign(requestParams, cursorPart)
            : requestParams),
        });

        if (response.status !== 200) {
          throw new HttpError(
            new Response(response.body as BodyInit | null, {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            }),
          );
        }

        return {
          cursor: extractCursor(
            response as Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
          ),
          items: extractItems(
            response as Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
          ),
        };
      },
      {
        ...(defaultOptions.queries as ModifiedQueriesOptions),
        suspense: true,
        keepPreviousData: true,
        useErrorBoundary: true,
        refetchInterval,
        getNextPageParam: ({ cursor }) => cursor,
        networkMode: "always",
        ...context?.queryOptions,
      },
    );

    const items = useMemo(
      () => data!.pages.flatMap((page) => page.items),
      [data],
    );

    const refetch = useCallback(async () => {
      await baseRefetch();
    }, [baseRefetch]);

    const fetchMore = useCallback(() => fetchNextPage(), [fetchNextPage]);

    return {
      items,
      isFetching,
      isFetchingMore: isFetchingNextPage,
      hasMore: Boolean(hasNextPage),
      fetchMore,
      refetch,
    };
  };

  const useOnDemandCursorQuery = <
    Url extends {
      [Url in keyof Api]: Extract<
        Api[Url][keyof Api[Url]]["response"],
        { status: 200 }
      > extends never
        ? never
        : Url;
    }[keyof Api],
    Method extends keyof Api[Url],
    Item,
  >(
    request:
      | ({ url: Url; method: Method } & {
          [K in keyof Api[Url][Method]["request"]]: Api[Url][Method]["request"][K];
        })
      | undefined,
    {
      insertCursor,
      extractCursor,
      extractItems,
      refetchInterval,
    }: {
      insertCursor: (
        cursor?: string,
      ) =>
        | DeepPartial<Omit<Api[Url][Method]["request"], "url" | "method">>
        | undefined;
      extractCursor: (
        response: Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
      ) => string | undefined;
      extractItems: (
        response: Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
      ) => Item[] | undefined;

      refetchInterval?: number | false;
    },
  ): {
    items: ReturnType<typeof extractItems> | undefined;
    isFetching: boolean;
    isFetchingMore: boolean;
    hasMore: boolean;
    fetchMore: () => void;
    refetch: () => Promise<void>;
  } => {
    const context = useContext(ApiContext);

    const hasRequest = Boolean(request);

    const {
      data,
      isFetching,
      hasNextPage,
      isFetchingNextPage,
      refetch: baseRefetch,
      fetchNextPage,
    } = useInfiniteQuery(
      request
        ? [
            queryKey,
            "cursor",
            request.url,
            request.method,
            request.body,
            request.headers,
            request.path,
            request.query,
          ]
        : [],
      async ({ pageParam: cursor }: { pageParam?: string }) => {
        if (!request) {
          throw new Error("fetcher has been called with undefined request");
        }

        const { url, method, body, headers, path, query } = request;

        const cursorPart = insertCursor(cursor);

        const requestParams = structuredClone({
          body,
          headers,
          path,
          query,
        }) as Omit<Api[Url][Method]["request"], "url" | "method">;

        const response = await fetcher({
          baseUrl,
          url,
          method,
          fetchModifier: context?.fetch,
          ...(cursorPart
            ? deepAssign(requestParams, cursorPart)
            : requestParams),
        });

        if (response.status !== 200) {
          throw new HttpError(
            new Response((response.body || null) as BodyInit | null, {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            }),
          );
        }

        return {
          cursor: extractCursor(
            response as Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
          ),
          items: extractItems(
            response as Extract<ApiResponse<Api, Url, Method>, { status: 200 }>,
          ),
        };
      },
      {
        ...(defaultOptions.queries as ModifiedQueriesOptions),
        enabled: hasRequest,
        refetchInterval,
        suspense: false,
        useErrorBoundary: true,
        keepPreviousData: true,
        getNextPageParam: ({ cursor }) => cursor,
        networkMode: "always",
        ...context?.queryOptions,
      },
    );

    const items = useMemo(
      () => data?.pages.flatMap((page) => page.items ?? []),
      [data],
    );

    const refetch = useCallback(async () => {
      if (hasRequest) {
        await baseRefetch();
      }
    }, [baseRefetch, hasRequest]);

    const fetchMore = useCallback(() => fetchNextPage(), [fetchNextPage]);

    return {
      items,
      isFetching: isFetching,
      isFetchingMore: isFetchingNextPage,
      hasMore: Boolean(hasNextPage),
      fetchMore,
      refetch,
    };
  };

  const Api = ({
    children,
    fetch,
    options,
    onActionError,
  }: {
    children: ReactNode;
    fetch?: ApiContextType["fetch"];
    options?: {
      enableDefaultStaleTime?: boolean;
    };

    /**
     * A callback that will be called when an error occurs in useAction.
     * When provided, errors will not be thrown to ErrorBoundary and will be propagated to this callback instead.
     */
    onActionError?: (error: Error) => void;
  }) => {
    return (
      <ApiContext.Provider
        value={{
          fetch,
          queryOptions: {
            staleTime: options?.enableDefaultStaleTime ? 30_000 : undefined,
          },
          onActionError,
        }}
      >
        {children}
      </ApiContext.Provider>
    );
  };

  return {
    useQuery,
    useOnDemandQuery,
    useAction,
    useCursorQuery,
    useOnDemandCursorQuery,
    Api,
  };
};
